import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
} from '@mui/material';
import { useCommercialPlan } from 'contexts';
import { IFontRuleViewModel } from 'viewModels';
import { ConsumptionFactorType } from 'types/enums';
import { IUpdateFontRuleInputModel } from 'inputModels';
import { CurrencyInput } from 'components';

interface EditFontRuleModalProps {
  open: boolean;
  onClose: () => void;
  planId: string;
  rule: IFontRuleViewModel;
  onSuccess: () => void;
}

const EditFontRuleModal: React.FC<EditFontRuleModalProps> = ({
  open,
  onClose,
  planId,
  rule,
  onSuccess,
}) => {
  const { handleUpdateFontRule } = useCommercialPlan();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<IUpdateFontRuleInputModel>(
    {
      factorType: ConsumptionFactorType.PER_EXECUTION,
      value: 0,
      description: '',
    }
  );

  useEffect(() => {
    if (rule) {
      setFormData({
        factorType: rule.factorType,
        value: rule.value,
        description: rule.description || '',
      });
    }
  }, [rule]);

  const handleInputChange = (
    field: keyof IUpdateFontRuleInputModel,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (formData.value < 0) {
      setError('Valor deve ser maior ou igual a zero');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await handleUpdateFontRule(
        planId,
        rule.fontKey,
        formData
      );
      if (success) {
        onSuccess();
      } else {
        setError('Erro ao atualizar regra de fonte');
      }
    } catch (err) {
      setError('Erro ao atualizar regra de fonte');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const getFactorTypeLabel = (factorType: ConsumptionFactorType) => {
    switch (factorType) {
      case ConsumptionFactorType.PER_EXECUTION:
        return 'Por Execução';
      case ConsumptionFactorType.PER_ITEM:
        return 'Por Item';
      case ConsumptionFactorType.PER_TIME:
        return 'Por Tempo';
      default:
        return 'Desconhecido';
    }
  };

  const getValueLabel = (factorType: ConsumptionFactorType) => {
    switch (factorType) {
      case ConsumptionFactorType.PER_EXECUTION:
        return 'Valor por execução (R$)';
      case ConsumptionFactorType.PER_ITEM:
        return 'Valor por item (R$)';
      case ConsumptionFactorType.PER_TIME:
        return 'Valor por segundo (R$)';
      default:
        return 'Valor';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar Regra de Fonte</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {formData.factorType === ConsumptionFactorType.PER_ITEM && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Atenção:</strong> O tipo de cobrança "Por Item"
            requer que a fonte esteja preparada com metadados
            específicos para efetuar o cálculo correto. Certifique-se
            de que a fonte possui a estrutura necessária antes de
            aplicar esta regra.
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 1,
          }}
        >
          <TextField
            label="Chave da Fonte"
            value={rule.fontKey}
            fullWidth
            disabled
            helperText="Chave da fonte não pode ser alterada"
          />

          <FormControl fullWidth disabled={loading}>
            <InputLabel>Tipo de Fator</InputLabel>
            <Select
              value={formData.factorType}
              onChange={(e) =>
                handleInputChange('factorType', e.target.value)
              }
              label="Tipo de Fator"
            >
              <MenuItem value={ConsumptionFactorType.PER_EXECUTION}>
                {getFactorTypeLabel(
                  ConsumptionFactorType.PER_EXECUTION
                )}
              </MenuItem>
              <MenuItem value={ConsumptionFactorType.PER_ITEM}>
                {getFactorTypeLabel(ConsumptionFactorType.PER_ITEM)}
              </MenuItem>
              <MenuItem value={ConsumptionFactorType.PER_TIME}>
                {getFactorTypeLabel(ConsumptionFactorType.PER_TIME)}
              </MenuItem>
            </Select>
          </FormControl>

          <CurrencyInput
            label={getValueLabel(formData.factorType)}
            value={formData.value}
            onChange={(value) => handleInputChange('value', value)}
            disabled={loading}
            helperText="Valor em reais"
          />

          <TextField
            label="Descrição"
            value={formData.description}
            onChange={(e) =>
              handleInputChange('description', e.target.value)
            }
            fullWidth
            multiline
            rows={3}
            disabled={loading}
            helperText="Descrição opcional da regra"
            slotProps={{
              htmlInput: {
                maxLength: 400,
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditFontRuleModal;
